import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { getUnlocalizedPath } from 'utils/localizedRouting';
import { CloseIcon, CasinoIcon, LogoIcon, SportsIcon } from 'components/icons';
import { navItems } from 'components/subHeader/subHeader';
import styles from './mobileMenu.module.scss';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const currentPath = getUnlocalizedPath(location.pathname);
  const activeTab = currentPath.startsWith('/sports') ? 'sports' : 'casino';

  const handleTabClick = (tab: 'casino' | 'sports') => {
    if (tab === 'sports') {
      navigate('/sports');
    } else {
      navigate('/');
    }
    window.scrollTo(0, 0);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      <div className={`${styles.backdrop} ${isOpen ? styles.show : ''}`} onClick={onClose} />
      <div className={`${styles.menu} ${isOpen ? styles.open : ''}`}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <LogoIcon width={120} height={38} fill="#ffffff" />
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close mobile menu">
            <CloseIcon width="24" height="24" />
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'casino' ? styles.active : ''}`}
            onClick={() => handleTabClick('casino')}
          >
            <span className={styles.tabIcon}>
              <CasinoIcon width="18" height="18" />
            </span>
            {t('header.casino', 'Casino')}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'sports' ? styles.active : ''}`}
            onClick={() => handleTabClick('sports')}
          >
            <SportsIcon width="18" height="18" />
            {t('common.sports', t('header.sports', 'Sports'))}
          </button>
        </div>

        <div className={styles.itemList}>
          {navItems.map((item, index) => {
            const targetPath =
              item.name === 'Lobby'
                ? '/'
                : `/casino/categories/${item.name.toLowerCase().replace(/\s+/g, '_')}`;

            return (
              <button
                key={index}
                className={styles.item}
                onClick={() => {
                  navigate(targetPath);
                  onClose();
                  window.scrollTo(0, 0);
                }}
              >
                <span className={styles.icon}>{item.icon}</span>
                {t(`common.${item.key}`, t(`nav.${item.key}`, item.name))}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
